<!-- eslint-disable no-param-reassign -->
<script>
// @ts-check

import { getBankTransactions } from '@/api/banktransactions'
import { getCompanySettings } from '@/api/company'
import { makePaymentReduction } from '@/api/incomes'
import UnassignedBankTransactions from '@/components/UnassignedBankTransactions.vue'
import CheckField from '@/components/fields/CheckField.vue'
import BaseErrors from '@/components/base/BaseErrors.vue'
import LoadingSpinner from '@/components/base/LoadingSpinner.vue'
import OutgoingPaymentInformation from '@/components/business-account/OutgoingPaymentInformation.vue'
import DateField from '@/components/fields/DateField.vue'
import MoneyField from '@/components/fields/MoneyField.vue'
import InfoIcon from '@/components/icons/InfoIcon.vue'
import VatSelect from '@/components/vat/controls/VatSelect.vue'
import { countryGroup, reductionType } from '@/constants/entries'
import { useVuelidate } from '@/core/composables/useVuelidate'
import { entryStatus } from '@/core/constants'
import { reformatDateString } from '@/helpers/dates'
import { formatNumberToFinnishLocale } from '@/helpers/formatters'
import { scrollIntoError } from '@/helpers/validation-errors'
import { getDocumentDateRange } from '@/helpers/voucher-dates'
import { present as presentInfo } from '@/presenters/infoText'
import { useCompanyStore } from '@/store/company'
import {
  currentPage,
  isLastPage,
  paginationState,
  setPaginationData
} from '@/store/paginationHelpers'
import { fromMoney, sumMoneys, toMoney } from '@spl-company-oy/core'
import { requiredIf } from '@vuelidate/validators'
import { computedAsync } from '@vueuse/core'
import * as _ from 'lodash-es'
import { storeToRefs } from 'pinia'
import { mapGetters } from 'vuex'

export default {
  name: 'AddPaymentEventForm',
  components: {
    CheckField,
    MoneyField,
    DateField,
    UnassignedBankTransactions,
    LoadingSpinner,
    InfoIcon,
    BaseErrors,
    VatSelect,
    OutgoingPaymentInformation
  },
  props: {
    entry: {
      /** @type {import('vue').PropType<EntryResource>} */
      type: Object,
      required: true
    }
  },
  emits: ['error', 'success'],
  setup() {
    const companyStore = useCompanyStore()
    const { company } = storeToRefs(companyStore)

    return {
      ...useVuelidate(),
      settings: computedAsync(getCompanySettings, {}, { flush: 'pre' }),
      company
    }
  },
  validations() {
    return {
      paid_date: {
        required: requiredIf(function () {
          return this.isOtherPaymentMethod
        })
      },
      amount: {
        required: requiredIf(function () {
          return this.isOtherPaymentMethod
        }),
        // This validator actually never fails because input does not allow
        // negative values.
        numberIsPositive: (value) => {
          return value >= 0
        },
        amountLessOrEqualToOutstanding(amount) {
          return amount <= this.entry.outstanding_sum
        },
        paymentLessOrEqualAvailableAmount(amount) {
          if (
            this.isBankTransactionPaymentMethod &&
            this.selectedBankTransactions.length === 1
          ) {
            return amount <= this.selectedBankTransactions[0].amount
          }
          return true
        }
      },
      selectedBankTransactions: {
        selectedSomeBankTransactionWhenNotInvoice: requiredIf(function () {
          return !this.isInvoice && this.isBankTransactionPaymentMethod
        }),
        selectedSomeBankTransactionOrReductionWhenInvoice: requiredIf(
          function () {
            return (
              this.isInvoice &&
              this.isBankTransactionPaymentMethod &&
              !this.hasPaymentReduction
            )
          }
        )
      },
      paymentReduction: {
        type: {
          required: requiredIf(function () {
            return this.hasPaymentReduction
          })
        },
        country: {
          required: requiredIf(function () {
            return this.hasPaymentReduction
          })
        },
        vat: {
          required: requiredIf(function () {
            return this.hasPaymentReduction
          })
        }
      }
    }
  },
  data() {
    return {
      /** @type {?string} */
      paymentMethod: '',
      paymentReduction: {
        type: 'DISCOUNT',
        country: 'finland',
        vat: 0
      },
      hasPaymentReduction: false,
      staticPaymentReductionAmount: 0,
      /** @type {?number} */
      amount: 0,
      paid_date: null,
      /** @type {BankTransactions} */
      unassignedBankTransactions: [],
      unassignedBankTransactionsDisabled: false,
      loadingBankTransactions: false,
      /** @type {BankTransactions} */
      selectedBankTransactions: [],
      errorWhileLinking: null,
      errorWhileFetchingBankTransactions: false,
      isSavingPayment: false,
      disabledFrom: null,
      disabledUntil: null,
      markedToBePaidAmountInputDisabled: false,
      search: '',
      limit: 4,
      ...paginationState
    }
  },
  computed: {
    ...mapGetters('app', ['isOyService']),
    isLastPage,
    currentPage,
    isOtherPaymentMethod() {
      return this.paymentMethod === 'cash'
    },
    isPartiallyPaid() {
      return this.entry.status === 'PARTIAL_PAID'
    },
    isBankTransactionPaymentMethod() {
      return this.paymentMethod === 'bank_transaction'
    },
    isEntirelyPaymentReduction() {
      return this.hasPaymentReduction && this.amount === 0
    },
    customerName() {
      const { customer } = this.entry

      if (!customer) {
        return null
      }

      if (customer.company_name) {
        return customer.company_name
      }

      return `${customer.first_name} ${customer.last_name}`
    },
    formattedTotalWithVat() {
      return formatNumberToFinnishLocale(this.entry.total_with_vat)
    },
    isInvoice() {
      return this.entry.entry_type === 'INVOICE'
    },
    dueDate() {
      return reformatDateString(this.entry.due_date)
    },
    entryDate() {
      return reformatDateString(this.entry.date)
    },
    bankTransactionsEnabled() {
      return (
        this.company.is_psd2_connected ||
        this.company.is_ws_connected ||
        this.company.swan_status === 'connected'
      )
    },
    hasCashPaymentsEnabled() {
      return this.settings.cash_payments_enabled === '1'
    },
    paymentEventTypes() {
      const paymentEventTypes = []

      if (this.isOyService) {
        if (this.hasCashPaymentsEnabled) {
          paymentEventTypes.push({
            label: this.$t('entries.actions.payment_event_type.ltd_cash.title'),
            subtitle: this.$t(
              `entries.actions.payment_event_type.ltd_cash.subtitle.${this.entry.type}`
            ),
            value: 'cash'
          })
        }
      } else {
        paymentEventTypes.push({
          label: this.$t('entries.actions.payment_event_type.cash.title'),
          value: 'cash',
          subtitle: this.$t(
            `entries.actions.payment_event_type.cash.subtitle.${this.entry.type}`
          )
        })
      }

      if (this.bankTransactionsEnabled) {
        paymentEventTypes.push({
          label: this.$t(
            'entries.actions.payment_event_type.bank_transaction.title'
          ),
          value: 'bank_transaction',
          subtitle: this.$t(
            `entries.actions.payment_event_type.bank_transaction.subtitle`
          )
        })
      }

      return paymentEventTypes
    },
    paymentReductionTypes() {
      const options = Object.values(reductionType).map((reduction) => ({
        label: this.$t(`payment_reduction.type.${reduction.toLowerCase()}`),
        value: reduction
      }))

      options.push({
        label: this.$t(`payment_reduction.type.financing_expense`),
        value: 'FINANCING_EXPENSE'
      })

      return options
    },
    paymentReductionCountryOptions() {
      return Object.values(countryGroup).map((group) => ({
        label: this.$t(`payment_reduction.country.${group}`),
        value: group
      }))
    },
    markAsPaidTotal() {
      if (this.isOtherPaymentMethod) {
        return this.amount
      }

      if (!this.hasBankTransactions) {
        return 0
      }

      return +fromMoney(
        this.selectedBankTransactions.reduce(
          (total, { amount: transactionAmount }) =>
            sumMoneys(total, toMoney(transactionAmount || 0)),
          toMoney(0)
        )
      )
    },
    markAsPaidTotalFinnishFormat() {
      return formatNumberToFinnishLocale(this.markAsPaidTotal ?? 0)
    },
    hasBankTransactions() {
      return (
        this.selectedBankTransactions?.length ||
        this.unassignedBankTransactions?.length
      )
    },
    getLinkingErrorTexts() {
      const errorTexts = this.getValidationMessages([
        'selectedBankTransactions'
      ])

      if (this.errorWhileLinking && this.isBankTransactionPaymentMethod) {
        errorTexts.push(this.getLinkingErrorText())
      }

      return errorTexts
    },
    infoText() {
      return presentInfo({
        disabledFrom: this.disabledFrom,
        disabledUntil: this.disabledUntil
      }).paymentInfo
    },
    amountThatWillRemainOpen() {
      return this.entry.outstanding_sum - (this.amount ?? 0)
    },
    showExistingPaymentInfo() {
      return this.entry.status === entryStatus.PAYMENT_PROCESSING
    }
  },
  watch: {
    paymentMethod: {
      immediate: true,
      async handler(newState) {
        if (newState !== 'bank_transaction') {
          this.amount = this.entry.outstanding_sum
          return
        }

        this.resetBankTransactionsFormInputs()
        await this.updateBankTransactions()
      }
      // Set default payment amount based on payment method
    }
  },
  async created() {
    if (!this.isOyService) {
      this.markedToBePaidAmountInputDisabled = true
    }

    this.paymentMethod = this.bankTransactionsEnabled
      ? 'bank_transaction'
      : 'cash'

    if (this.company.is_in_demo_mode) {
      return
    }

    const { min, max } = await getDocumentDateRange()

    this.disabledUntil = min
    this.disabledFrom = max
  },
  methods: {
    setPaginationData,
    formatNumberToFinnishLocale,
    getValidationMessages(fields) {
      return fields.flatMap((field) => this.getValidationMessage(field) ?? [])
    },
    getLinkingErrorText() {
      const { response = {} } = this.errorWhileLinking
      const { status = 200, data = {} } = response

      return status === 400
        ? this.$t(`entries.actions.${data.error.error}`)
        : this.$t('entries.actions.error.linking_failed')
    },
    async submitPaymentEvent() {
      this.isSavingPayment = true
      this.errorWhileLinking = null

      if (!this.validateOnSubmit()) {
        scrollIntoError(this)
        // Need to clear all loaders etc.
        this.isSavingPayment = false
        this.$emit('error')
        return
      }
      this.staticPaymentReductionAmount = this.amountThatWillRemainOpen
      if (!this.isEntirelyPaymentReduction) {
        if (this.isOtherPaymentMethod) {
          try {
            await this.markAsPaidElsewhere()
          } catch (error) {
            this.setServerValidationMessages(error)
            this.$emit('error', error)
            this.isSavingPayment = false
          }
        }
        if (this.isBankTransactionPaymentMethod) {
          try {
            await this.linkBankTransactionsToEntry()
          } catch (error) {
            console.error(error)
            this.errorWhileLinking = error
            this.$emit('error', error)
            this.isSavingPayment = false
          }
        }
      }
      if (this.hasPaymentReduction) {
        try {
          await this.makePaymentReduction()
        } catch (error) {
          this.setServerValidationMessages(error)
          this.$emit('error', error)
          this.isSavingPayment = false
        }
      }
      this.isSavingPayment = false
      this.$emit('success')
    },
    async markAsPaidElsewhere() {
      return this.$store.dispatch(`${this.entry.type}s/markAsPaid`, {
        id: this.entry.id,
        type: this.entry.type,
        payload: { date: this.paid_date, amount: this.amount }
      })
    },
    async linkBankTransactionsToEntry() {
      return this.$store.dispatch(
        `${this.entry.type}s/linkBankTransactionsToEntry`,
        {
          id: this.entry.id,
          type: this.entry.type,
          payload: {
            bankTransactions: this.selectedBankTransactions.map(
              (transaction) => ({
                id: transaction.id,
                amount: transaction.amount
              })
            ),
            amount: this.amount
          }
        }
      )
    },
    async makePaymentReduction() {
      const { data } = await makePaymentReduction({
        resource_id: this.entry.id,
        resource_type: this.entry.type,
        amount: this.staticPaymentReductionAmount,
        type: this.paymentReduction.type,
        country: this.paymentReduction.country,
        vat: this.paymentReduction.vat
      })

      this.$store.commit(`${this.entry.type}s/updateEntry`, {
        id: this.entry.id,
        data
      })
    },
    updateSelectedBankTransactions({ transaction, isSelected }) {
      const index = this.selectedBankTransactions.findIndex(
        (selectedTransaction) => selectedTransaction.id === transaction.id
      )

      // if there is no unassigned bank transaction already selected
      if (index === -1 && isSelected) {
        this.selectedBankTransactions.push(transaction)
      }

      // if a previously selected unassigned banktransaction is deselected
      if (index >= 0 && !isSelected) {
        this.selectedBankTransactions.splice(index, 1)
      }

      if (this.isOyService) {
        this.markedToBePaidAmountInputDisabled =
          this.selectedBankTransactions.length > 1

        this.unassignedBankTransactionsDisabled = false
      }

      this.amount = this.markAsPaidTotal
    },
    isCustomMarkedToBePaidAmountSet(inputValue) {
      // Early return if input value is empty (null)
      if (typeof inputValue !== 'number') {
        return
      }

      if (this.selectedBankTransactions.length === 1) {
        const selectedAmount = this.selectedBankTransactions[0]?.amount ?? null

        if (
          typeof selectedAmount === 'number' &&
          selectedAmount !== inputValue
        ) {
          // If user gives custom amount, disable selection for all other unchecked transactions
          this.unassignedBankTransactionsDisabled = true
        } else {
          // Enable checkboxes for all unassigned bank transactions when amounts are same
          this.unassignedBankTransactionsDisabled = false
        }
      }
    },
    resetBankTransactionsFormInputs(resetSearch = true) {
      this.unassignedBankTransactionsDisabled = false
      this.amount = 0
      if (resetSearch) {
        this.search = ''
      }
      this.selectedBankTransactions = []
      if (this.isOyService) {
        this.markedToBePaidAmountInputDisabled = false
      }
    },
    async updateBankTransactions(nextPage = false) {
      this.loadingBankTransactions = true
      this.errorWhileFetchingBankTransactions = false

      const page = nextPage ? this.currentPage + 1 : 1

      const payload = {
        limit: this.limit,
        assigned: false,
        open_period: true,
        entry_type: this.entry.type
      }

      if (this.search) {
        Object.assign(payload, { search: this.search })
      }

      if ((this.search && page === 1) || page === 1) {
        this.resetBankTransactionsFormInputs(false)
      }

      try {
        const { data, meta } = await getBankTransactions(payload, page)

        let transactions = nextPage
          ? this.unassignedBankTransactions.concat(data)
          : data

        if (page === 1 && !this.search) {
          const amount =
            this.entry.type === 'expense'
              ? -this.entry.outstanding_sum
              : this.entry.outstanding_sum

          const { data: priorityTransactions } = await getBankTransactions({
            limit: 3,
            amount,
            assigned: false,
            open_period: true,
            entry_type: this.entry.type
          })
          transactions = transactions.concat(priorityTransactions)
        }

        this.unassignedBankTransactions = _.uniqBy(transactions, 'id')

        this.setPaginationData(meta)
      } catch (error) {
        console.error(error)
        this.errorWhileFetchingBankTransactions = true
      } finally {
        this.loadingBankTransactions = false
      }
    },
    searchBankTransactions: _.debounce(async function () {
      await this.updateBankTransactions()
    }, 1000)
  }
}
</script>

<template>
  <div>
    <!-- Entry information (invoice) -->
    <table
      v-if="entry.type === 'income' && entry.invoice"
      class="entry-details mb-4 w-full table-fixed"
    >
      <tbody>
        <tr>
          <th scope="row">{{ $t('mark_as_paid.entry.invoice_number') }}</th>
          <td>{{ entry.invoice.invoice_number }}</td>
        </tr>
        <tr>
          <th scope="row">
            {{ $t('mark_as_paid.entry.invoice_correspondent') }}
          </th>
          <td>{{ customerName }}</td>
        </tr>
        <tr>
          <th scope="row">{{ $t('mark_as_paid.entry.amount') }}</th>
          <td>{{ formattedTotalWithVat }}€</td>
        </tr>
        <tr>
          <th scope="row">{{ $t('mark_as_paid.entry.due_date') }}</th>
          <td>{{ dueDate }}</td>
        </tr>
      </tbody>
    </table>

    <!-- Entry information (non invoice) -->
    <table v-else class="entry-details mb-4 w-full table-fixed">
      <tbody>
        <tr>
          <th scope="row">{{ $t('mark_as_paid.entry.date') }}</th>
          <td>{{ entryDate }}</td>
        </tr>
        <tr>
          <th scope="row">{{ $t('mark_as_paid.entry.amount') }}</th>
          <td>{{ formattedTotalWithVat }}€</td>
        </tr>
        <tr>
          <th scope="row">{{ $t('mark_as_paid.entry.description') }}</th>
          <td>{{ entry.description }}</td>
        </tr>
      </tbody>
    </table>

    <InfoAlert v-if="!bankTransactionsEnabled && !isOyService" class="mb-4">
      <I18nT
        keypath="entries.actions.mark_as_paid.bank_connection_info"
        tag="p"
        scope="global"
      >
        <template #link>
          <RouterLink
            :to="{ name: 'bank_accounts' }"
            class="font-medium text-primary"
          >
            {{ $t('entries.actions.mark_as_paid.link_bank_account') }}
          </RouterLink>
        </template>
      </I18nT>
    </InfoAlert>

    <InfoAlert
      v-if="bankTransactionsEnabled && isOtherPaymentMethod && !isOyService"
      class="mb-4"
    >
      <p>
        {{ $t(`entries.actions.payment_event_type.cash.info.${entry.type}`) }}
      </p>
    </InfoAlert>

    <OutgoingPaymentInformation v-if="showExistingPaymentInfo" class="mt-4" />

    <!-- Bank transaction linking -->
    <form
      class="grid-content mt-6 md:mt-4 md:grid-cols-2"
      @submit.prevent="submitPaymentEvent"
    >
      <VSelect
        v-if="bankTransactionsEnabled"
        v-model="paymentMethod"
        :menu-props="{
          maxWidth: 0
        }"
        :items="paymentEventTypes"
        :label="$t('entries.actions.mark_as_paid.payment_channel')"
        class="required col-span-full w-full"
        item-title="label"
        item-value="value"
        item-props
      />

      <template v-if="isOtherPaymentMethod">
        <DateField
          v-model="v$.paid_date.$model"
          class="required"
          :label="$t('entries.actions.mark-as-paid-date')"
          :error-messages="errors.paid_date"
          :min="disabledUntil"
          :max="disabledFrom"
        >
          <template #append-inner>
            <InfoIcon v-tooltip="infoText" />
          </template>
        </DateField>
        <MoneyField
          v-model="v$.amount.$model"
          :label="$t('entries.actions.mark-as-paid-amount')"
          class="required"
          :error-messages="errors.amount"
        />
      </template>

      <div
        v-if="isBankTransactionPaymentMethod"
        id="unassigned-bank-transactions"
        class="grid-content col-span-full"
      >
        <h3 class="my-1 text-xs md:text-sm">
          {{ $t('entries.actions.mark_as_paid.unassigned_bank_transaction') }}
        </h3>

        <!-- Search by name or amount -->
        <VTextField
          v-model="search"
          :label="$t('general.search')"
          :placeholder="$t('entries.actions.mark_as_paid.search_label')"
          autocomplete="off"
          type="search"
          :disabled="unassignedBankTransactionsDisabled"
          @update:model-value="searchBankTransactions()"
        >
          <template #append-inner>
            <LoadingSpinner v-if="loadingBankTransactions" :size="16" />
            <VIcon v-else icon="$magnify" />
          </template>
        </VTextField>
        <div
          v-if="
            !loadingBankTransactions && unassignedBankTransactions.length === 0
          "
          class="text-center"
        >
          {{
            $t(
              'entries.actions.mark_as_paid.unassigned_bank_transaction_not_found'
            )
          }}
        </div>
        <table class="w-full">
          <!-- entry.total_with_vat -->
          <UnassignedBankTransactions
            v-if="unassignedBankTransactions.length"
            :transactions="unassignedBankTransactions"
            :selected-bank-transactions
            :total-amount="entry.outstanding_sum"
            :checkboxes-disabled="unassignedBankTransactionsDisabled"
            @select="updateSelectedBankTransactions($event)"
          />
          <tfoot>
            <tr v-if="unassignedBankTransactions.length > 0 && !isLastPage">
              <td colspan="4">
                <button
                  type="button"
                  class="flex w-full items-center justify-center border-b border-secondary-300 bg-off-white py-3 text-xs font-medium text-gray-700"
                  @click="updateBankTransactions(true)"
                >
                  <VIcon icon="$plus" class="mr-2" />
                  <span>
                    {{ $t('general.show_more') }}
                  </span>
                  <LoadingSpinner
                    v-show="loadingBankTransactions"
                    :size="16"
                    class="ml-2"
                  />
                </button>
              </td>
            </tr>
            <tr v-if="errorWhileFetchingBankTransactions">
              <td colspan="4">
                <DangerAlert>
                  <p>
                    {{
                      $t(
                        'entries.actions.mark_as_paid.failed_fetching_bank_transaction'
                      )
                    }}
                  </p>
                </DangerAlert>
              </td>
            </tr>
          </tfoot>
        </table>
        <MoneyField
          v-model="amount"
          :label="$t('entry_action.mark-as-paid-amount')"
          :disabled="markedToBePaidAmountInputDisabled"
          :error-messages="getValidationMessage('amount')"
          @update:model-value="isCustomMarkedToBePaidAmountSet($event)"
        />
      </div>
    </form>
    <div class="mt-4">
      <div class="grid grid-cols-1 content-center md:grid-cols-2">
        <p class="w-full">{{ $t('entries.actions.mark-as-paid-open') }}:</p>
        <span class="w-full md:text-right">
          {{ formatNumberToFinnishLocale(entry.outstanding_sum) }} €
        </span>
      </div>
      <div
        v-if="paymentMethod === 'cash'"
        class="mt-2 grid grid-cols-1 content-center md:grid-cols-2"
      >
        <p class="w-full self-center">
          {{ $t('entries.actions.mark_as_paid_total') }}:
        </p>
        <span class="w-full self-center md:text-right">
          {{ markAsPaidTotalFinnishFormat }} €
        </span>
      </div>
      <div class="mt-2 grid grid-cols-1 content-center md:grid-cols-2">
        <p class="w-full">{{ $t('entry_action.mark-as-paid-remain-open') }}:</p>
        <span class="w-full md:text-right">
          {{ formatNumberToFinnishLocale(amountThatWillRemainOpen) }} €
        </span>
      </div>
    </div>

    <template v-if="isOyService">
      <!-- Is payment reduction checkbox -->
      <CheckField
        v-if="
          amountThatWillRemainOpen > 0 &&
          isInvoice &&
          ((amount && amount > 0) || isPartiallyPaid)
        "
        v-model="hasPaymentReduction"
        class="mt-4 md:mt-6"
      >
        {{ $t('entry_action.payment-reduction-check') }}
      </CheckField>

      <!-- If entry will remain open / Payment Reduction -->
      <template v-if="hasPaymentReduction">
        <div class="mt-4 md:mt-6">
          <h3 class="heading-3 mb-2">
            {{ $t('entries.actions.payment-reduction.title') }}
          </h3>
          <p>
            {{ $t('entries.actions.payment-reduction.description') }}
          </p>
        </div>
        <div class="grid-controls mt-4 md:mt-6 md:grid-cols-2">
          <!-- Type -->
          <VSelect
            v-model="paymentReduction.type"
            :items="paymentReductionTypes"
            :label="$t('entry_action.remaining-amount-type-question')"
            class="col-span-full"
            item-title="label"
            item-value="value"
            :menu-props="{
              maxWidth: 0
            }"
            item-props
          />

          <VSelect
            v-model="paymentReduction.country"
            :items="paymentReductionCountryOptions"
            :label="$t('form.label.reduction_country')"
            class="w-full"
            item-title="label"
            item-value="value"
            :menu-props="{
              maxWidth: 0
            }"
            item-props
          />

          <VatSelect
            v-model="paymentReduction.vat"
            :disabled="paymentReduction.type === 'FINANCING_EXPENSE'"
            class="flex w-full flex-col"
          />
        </div>
      </template>
    </template>

    <!-- Errors -->
    <BaseErrors
      v-if="getLinkingErrorTexts.length"
      class="mt-4"
      :errors="getLinkingErrorTexts"
    />
    <slot :submit="submitPaymentEvent" :saving="isSavingPayment" />
  </div>
</template>
